<template>
  <li
    ref="root"
    class="p-2 flex items-center rounded-8 gap-4 cursor-pointer justify-between whitespace-nowrap"
    :class="{
      'bg-charleston-green': isHovered
    }"
    @mouseenter="onMouseEnter()"
    @mouseleave="onMouseLeave()"
  >
    <div class="flex items-center gap-4">
      <div class="relative">
        <contact-avatar
          :image="null"
          :name="guest.name"
        />
        <contact-status
          :p-served="guest.pServed"
          compact-mode
          for-participant-in-chat
        />
      </div>
      <div class="flex flex-col justify-between py-1 min-w-0">
        <name-with-user-agent
          class="text-surf-lock-wht text-body-r-14-20"
          :name="guest.name"
          :user-agent="participantUserAgents"
        />
        <div
          v-if="statusLabel"
          class="text-label-r-13-18 text-surf-lock-wht/70 mt-2"
        >
          {{ statusLabel }}
        </div>
      </div>
    </div>
    <div class="flex items-center justify-end">
      <div
        v-show="(!isHovered && !menuOpened) || currentGuest"
        class="text-1316 text-vista-blue"
      >
        {{ $t('common.guest') }} <template v-if="guest.pServed === currentGuest?.pServed">
          ({{ $t('common.you') }})
        </template>
      </div>
      <div
        v-show="(isHovered || menuOpened) && !currentGuest"
        class="h-10 flex items-center justify-end"
      >
        <wui-dropdown
          v-model="menuOpened"
          left-bottom
          close-on-click-inside
        >
          <template #activator="{ toggle }">
            <wui-btn
              class="bg-police-blue! text-black-200! hover:opacity-70 relative shrink-0!"
              prepend-icon="kebabHorizontalM"
              icon
              :size="32"
              @click="toggle"
            />
          </template>
          <template #default>
            <call-session-guest-menu
              :guest="guest"
              :room="room"
              :is-pinned="!!isPinned"
              @toggle-pin="emit('toggle-pin', guest)"
            />
          </template>
        </wui-dropdown>
      </div>
    </div>
  </li>
</template>

<script setup lang="ts">
import { WuiDropdown, WuiBtn } from '@wui/common-library'
import { onMounted, onUnmounted, ref, computed, toRefs, watch } from 'vue'


import { CallSessionGuestMenu } from '@/features/call-session-guest-menu'

import { Guest, useStoreConferenceMediaStatuses } from '@/entities/conference-participant'
import { ContactAvatar } from '@/entities/contact-avatar'
import { MemberStatus } from '@/entities/room'

import { NameWithUserAgent } from '@/shared/components/user-agent-icon'
import { useGuestAuth } from '@/shared/composables/guest-auth'
import { useStatusSubscribe } from '@/shared/composables/im'
import { useLocalization } from '@/shared/composables/localization'
import { ContactStatus } from '@/shared/contact-status'
import { useStoreGuestStatus } from '@/shared/guest-status'
import { Room } from '@/shared/types/store'

const props = defineProps<{
  guest: Guest,
  room: Room
  status: MemberStatus
  isPinned?: boolean
}>()

const emit = defineEmits<{
  (event: 'toggle-pin', value: Guest): void
}>()

const { guest } = toRefs(props)
const { currentGuest } = useGuestAuth()
const { t } = useLocalization()
const { subscribe, unsubscribe } = useStatusSubscribe()
const { getConferenceCallsByImLogin } = useStoreConferenceMediaStatuses()

const participantUserAgents = computed(() => [...new Set(
  getConferenceCallsByImLogin(props.room, guest.value.pServed)
    .map(participant => participant.userAgent)
    .filter(Boolean),
)])

useStoreGuestStatus()

onMounted(() => {
  if (guest.value.pServed) {
    subscribe(guest.value.pServed)
  }
})

onUnmounted(() => {
  if (guest.value.pServed) {
    unsubscribe(guest.value.pServed)
  }
})

watch(() => guest.value.pServed, (newValue, oldValue) => {
  if (oldValue) {
    unsubscribe(oldValue)
  }
  if (newValue) {
    subscribe(newValue)
  }
})

const isHovered = ref(false)
const root = ref()
const menuOpened = ref(false)

const statusLabel = computed<string>(() => {
  if (props.status === MemberStatus.Active) {
    return t('calls.inConference')
  }

  if (props.status === MemberStatus.Dialing) {
    return t('calls.joins')
  }

  return ''
})

const coolDown = ref(false)

const onMouseEnter = () => {
  isHovered.value = true
  coolDown.value = false
}

const onMouseLeave = () => {
  isHovered.value = false
  coolDown.value = true
  setTimeout(() => {
    if (coolDown.value) {
      menuOpened.value = false
    }
  }, 1000)
}
</script>

<style scoped>
.grid-cols-3-custom {
  grid-template-columns: auto 1fr auto;
}
</style>
